<template>
  <div class="adyen-payment">
    <div ref="cardContainer" class="adyen-payment__card-container"></div>
  </div>
</template>

<script lang="ts">
import { defineComponent, onMounted, onUnmounted, ref } from 'vue'
import { AdyenCheckout, Card } from '@adyen/adyen-web'
import type { PaymentData } from '@/types/paymentTypes'

export default defineComponent({
  name: 'AdyenCardPayment',
  emits: ['card-data', 'card-error', 'brand', 'field-valid'],
  props: {
    amount: {
      type: Number,
      required: true
    },
    currency: {
      type: String,
      required: true
    },
    bookingNumber: {
      type: String,
      required: true
    },
    customerEmail: {
      type: String,
      required: true
    },
    customerName: {
      type: String,
      required: true
    }
  },

  setup(props, { emit }) {
    const cardContainer = ref<HTMLElement | null>(null)
    let checkout: Awaited<ReturnType<typeof AdyenCheckout>> | null = null
    let card: Card | null = null

    const initializeAdyen = async () => {
      try {
        checkout = await AdyenCheckout({
          environment: import.meta.env.VITE_ADYEN_ENVIRONMENT,
          clientKey: import.meta.env.VITE_ADYEN_CLIENT_KEY,
          locale: "no-NO",
          countryCode: 'NO',
          amount: {
            value: Math.round(props.amount * 100),
            currency: props.currency
          }
        })

        const cardConfig = {
          hasHolderName: false,
          holderNameRequired: false,
          billingAddressRequired: false,
          enableStoreDetails: false,
          brands: ["visa", "mc", "amex"],
          showPayButton: false,
          onBrand: (brandInfo: any) => {
            if (brandInfo.brand) {
              emit('brand', brandInfo.brand)
            }
          },
          onFieldValid: (data: any) => {
            if (data.encryptedFieldName) {
              emit('field-valid', data.encryptedFieldName)
            }
          },
          onChange: (state: any) => {
            if (state.isValid) {
              const cardData = {
                encrypted_card_number: state.data?.paymentMethod?.encryptedCardNumber,
                encrypted_expiry_month: state.data?.paymentMethod?.encryptedExpiryMonth,
                encrypted_expiry_year: state.data?.paymentMethod?.encryptedExpiryYear,
                encrypted_security_code: state.data?.paymentMethod?.encryptedSecurityCode
              }

              if (cardData.encrypted_card_number && 
                  cardData.encrypted_expiry_month && 
                  cardData.encrypted_expiry_year && 
                  cardData.encrypted_security_code) {
                emit('card-data', cardData)
              }
            }
          },
          onError: (error: any) => {
            console.error('Card error:', error)
            emit('card-error', error)
          }
        }
        
        card = new Card(checkout, cardConfig)
        
        if (!card) {
          throw new Error('Failed to create card component')
        }

        if (!cardContainer.value) {
          throw new Error('Card container element not found')
        }

        card.mount(cardContainer.value)
      } catch (error) {
        console.error('Error initializing Adyen:', error)
        emit('card-error', error)
      }
    }

    onMounted(() => {
      if (props.amount > 0) {
        initializeAdyen()
      }
    })

    onUnmounted(() => {
      if (card) {
        card.unmount()
        card = null
      }
      checkout = null
    })

    return {
      cardContainer
    }
  }
})
</script>

<style lang="scss">
@import '@adyen/adyen-web/styles/adyen.css';

.adyen-payment {
  padding: 1rem;
  margin-top: 1rem;
  border: 1px solid $clr-primary-100;
  border-radius: $border-radius;

  
  &__card-container {
    width: 100%;
    max-width: 400px;
    display: flex;
    justify-content: flex-start;
  }
}
</style>